/* Progressive enhancement: photographs and every description remain visible without JavaScript. */
if (!customElements.get("support-explorer")) {
  customElements.define(
    "support-explorer",
    class extends HTMLElement {
      connectedCallback() {
        this.controller?.abort();
        this.tiltResets?.forEach((reset) => reset());
        this.tiltResets = [];
        this.controller = new AbortController();
        const { signal } = this.controller;
        const viewer = this.querySelector("[data-explore-viewer]");
        const zooms = [...this.querySelectorAll("[data-explore-zoom]")];
        if (viewer && typeof viewer.showModal === "function") {
          zooms.forEach((button) => {
            button.disabled = false;
            button.addEventListener(
              "click",
              () => {
                const source = button.querySelector("img");
                if (!source) return;
                const image = source.cloneNode();
                image.removeAttribute("srcset");
                image.removeAttribute("sizes");
                image.loading = "eager";
                image.removeAttribute("fetchpriority");
                viewer
                  .querySelector("[data-explore-viewer-image]")
                  .replaceChildren(image);
                viewer.querySelector(
                  "[data-explore-viewer-caption]",
                ).textContent = source.alt;
                this.zoomTrigger = button;
                viewer.showModal();
              },
              { signal },
            );
          });
          viewer
            .querySelector("[data-explore-close]")
            .addEventListener("click", () => viewer.close(), { signal });
          viewer.addEventListener(
            "click",
            (event) => {
              if (event.target !== viewer) return;
              const box = viewer.getBoundingClientRect();
              if (
                event.clientX < box.left ||
                event.clientX > box.right ||
                event.clientY < box.top ||
                event.clientY > box.bottom
              )
                viewer.close();
            },
            { signal },
          );
          viewer.addEventListener(
            "close",
            () => this.zoomTrigger?.focus({ preventScroll: true }),
            { signal },
          );
        }
        const buttons = [...this.querySelectorAll("[data-explore-select]")];
        const panels = [...this.querySelectorAll("[data-explore-panel]")];
        const status = this.querySelector("[data-explore-status]");
        if (!buttons.length || !panels.length) return;

        const select = (view, announce = true) => {
          const selected = panels.find(
            (panel) => panel.dataset.explorePanel === view,
          );
          if (!selected) return;
          this.tiltResets.forEach((reset) => reset());
          panels.forEach((panel) => {
            panel.hidden = panel !== selected;
          });
          buttons.forEach((button) => {
            button.setAttribute(
              "aria-pressed",
              String(button.dataset.exploreSelect === view),
            );
          });
          if (announce && status)
            status.textContent = `${selected.querySelector("h3")?.textContent.trim() || "Detail"} view selected.`;
        };

        buttons.forEach((button) => {
          button.addEventListener(
            "click",
            () => select(button.dataset.exploreSelect),
            { signal },
          );
          button.addEventListener(
            "keydown",
            (event) => {
              const group = [
                ...button.parentElement.querySelectorAll(
                  "[data-explore-select]",
                ),
              ];
              const index = group.indexOf(button);
              let next;
              if (event.key === "ArrowDown" || event.key === "ArrowRight")
                next = (index + 1) % group.length;
              if (event.key === "ArrowUp" || event.key === "ArrowLeft")
                next = (index - 1 + group.length) % group.length;
              if (event.key === "Home") next = 0;
              if (event.key === "End") next = group.length - 1;
              if (next === undefined) return;
              event.preventDefault();
              group[next].focus();
              select(group[next].dataset.exploreSelect);
            },
            { signal },
          );
        });

        select(panels[0].dataset.explorePanel, false);
        this.setAttribute("data-enhanced", "");

        const stage = this.querySelector("[data-explore-stage]");
        const photo = this.querySelector("[data-explore-photo]");
        const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
        const pointer = window.matchMedia("(hover: hover) and (pointer: fine)");
        const tiltTargets = [
          ...(stage && photo ? [{ target: stage, image: photo }] : []),
          ...this.querySelectorAll(".support-explore__zoom-photo"),
        ];
        tiltTargets.forEach((entry) => {
          const target = entry.target || entry;
          const image = entry.image || entry;
          let frame;
          const reset = () => {
            cancelAnimationFrame(frame);
            image.style.removeProperty("--tilt-x");
            image.style.removeProperty("--tilt-y");
          };
          this.tiltResets.push(reset);
          target.addEventListener(
            "pointermove",
            (event) => {
              if (
                motion.matches ||
                !pointer.matches ||
                event.pointerType === "touch"
              )
                return;
              const box = target.getBoundingClientRect();
              const x = Math.max(
                -1,
                Math.min(1, ((event.clientX - box.left) / box.width - 0.5) * 2),
              );
              const y = Math.max(
                -1,
                Math.min(1, ((event.clientY - box.top) / box.height - 0.5) * 2),
              );
              cancelAnimationFrame(frame);
              frame = requestAnimationFrame(() => {
                image.style.setProperty(
                  "--tilt-x",
                  `${(-y * 2).toFixed(2)}deg`,
                );
                image.style.setProperty("--tilt-y", `${(x * 2).toFixed(2)}deg`);
              });
            },
            { signal, passive: true },
          );
          target.addEventListener("pointerleave", reset, { signal });
          target.addEventListener("pointercancel", reset, { signal });
          target.addEventListener("click", reset, { signal });
        });
        const resetAll = () => this.tiltResets.forEach((reset) => reset());
        motion.addEventListener("change", resetAll, { signal });
        pointer.addEventListener("change", resetAll, { signal });
        document.addEventListener("visibilitychange", resetAll, { signal });
      }

      disconnectedCallback() {
        this.querySelector("[data-explore-viewer]")?.close();
        this.controller?.abort();
        this.tiltResets?.forEach((reset) => reset());
      }
    },
  );
}
